import {
  GraphData,
  UseWealthSimulatorCalculation,
  GetSimulation,
} from '@/components/WealthSimulator/types';

export const getSimulation: GetSimulation = (
  initialPatrimony,
  nbYears,
  annualInvestment,
  yieldValue,
  inflationRate,
  tax,
  compoundsPerYear = 1,
) => {
  const interestRate = (1 + yieldValue / 100) / (1 + inflationRate / 100) - 1;
  const futurCapital =
    interestRate <= 0
      ? initialPatrimony + annualInvestment * nbYears
      : initialPatrimony * Math.pow(1 + interestRate / compoundsPerYear, nbYears * compoundsPerYear) +
          (annualInvestment * (Math.pow(1 + interestRate / compoundsPerYear, nbYears * compoundsPerYear) - 1)) / interestRate || 0;
  const addedValue =
    interestRate <= 0 ? 0 : futurCapital - initialPatrimony - nbYears * annualInvestment;

  return {
    futurCapital,
    addedValue,
    netValue: interestRate <= 0 ? futurCapital : futurCapital - (addedValue / 100) * tax,
    isInterestRateInvalid: interestRate < 0,
  };
};

export const useWealthSimulatorCalculation: UseWealthSimulatorCalculation = ({
  patrimony,
  annualInvestment,
  allowance,
  nbYears,
  stockYield,
  yieldOther,
  investAllowance,
  stockTax,
  tax,
  withdrawalRate,
  inflationRate,
}) => {
  const {
    futurCapital: fcStock,
    addedValue: addedValueStock,
    netValue: netValueStock,
    isInterestRateInvalid: isInvalidStock,
  } = getSimulation(
    (patrimony / 100) * allowance, // Initial patrimony for stock
    nbYears,
    (annualInvestment / 100) * investAllowance, // Annual investment for stock
    stockYield,
    inflationRate,
    stockTax
  );
  const {
    futurCapital: fcOther,
    addedValue: addedValueOther,
    netValue: netValueOther,
    isInterestRateInvalid: isInvalidOther,
  } = getSimulation(
    (patrimony / 100) * (100 - allowance), // Initial patrimony for other
    nbYears,
    (annualInvestment / 100) * (100 - investAllowance), // Annual investment for other
    yieldOther,
    inflationRate,
    tax
  );
  const netValueTotal = (netValueStock || 0) + (netValueOther || 0);

  const graphData = Array.from(Array(Math.floor(nbYears)).keys()).reduce<GraphData>((acc, year) => {
    const yearNumber = year + 1;
    // Simulation for stock
    const { netValue: netValueStockN } = getSimulation(
      (patrimony / 100) * allowance, // Initial patrimony for stock
      yearNumber,
      (annualInvestment / 100) * investAllowance, // Annual investment for stock
      stockYield,
      inflationRate,
      stockTax
    );

    // simulation for other investment than stock
    const { netValue: netValueOtherN } = getSimulation(
      (patrimony / 100) * (100 - allowance), // Initial patrimony for other
      yearNumber,
      (annualInvestment / 100) * (100 - investAllowance), // Annual investment for other
      yieldOther,
      inflationRate,
      tax
    );

    return [
      ...acc,
      {
        initialPatrimony: patrimony,
        investment: annualInvestment * yearNumber + patrimony, // trick to have the lines stacked in the graph
        netValue: netValueStockN + netValueOtherN,
        year: yearNumber,
      },
    ];
  }, []);

  return {
    futurCapital: fcStock + fcOther,
    addedValue: addedValueStock + addedValueOther,
    netValue: netValueTotal,
    monthlyRevenue: ((netValueTotal / 100) * withdrawalRate) / 12,
    graphData,
    isInvalid: isInvalidStock || isInvalidOther,
  };
};
